import { Component, OnInit, ViewChild, ElementRef, Input, EventEmitter, AfterViewInit, Output, ContentChildren, QueryList } from '@angular/core';
import { Calendar } from 'primeng/calendar';
import { AppService } from 'app/app.service';
import { DataGuardService } from 'app/data-guard.service';

declare const $: any;

@Component({
	selector: 'ui-modal',
	template:
		`<div tabindex="-1" [ngClass]="{ modal: !embed, 'modal-overflow-visible': overflowVisible && !embed, 'fill': embed }" (click)="backdropClick($event)" #modal>
			<div [ngClass]="{ 'modal-dialog modal-dialog-centered': !embed, 'modal-size-sm': !embed && size === 'sm', 'modal-size-lg': !embed && size === 'lg', 'modal-size-xl': !embed && size === 'xl', 'modal-size-xxl': !embed && size === 'xxl', 'modal-dialog-scrollable': !embed && scrollable, 'fill': embed }">
				<div *ngIf="!customContent" class="border-0" [ngClass]="{ 'modal-content shadow': !embed, 'modal-full-height': fullHeight, 'fill d-flex flex-column': embed }">
					<div *ngIf="header" class="align-items-center border-0 pb-3" [ngClass]="{ 'modal-header px-4': !embed, 'd-flex': embed }" [ngStyle]="alignHeaderContentNearTitle && {'justify-content': 'unset'}">
						<button *ngIf="showBackButton" type="button" class="btn btn-outline-secondary border-0 py-2 px-1 mr-1 ml-n2" (click)="goBack()">
							<i class="wq wq-chevron-left xl"></i>
						</button>
						<div class="d-flex align-items-center" [ngClass]="{ 'flex-weight-1' : !alignHeaderContentNearTitle }">
							<h3 [ngClass]="{ 'modal-title': !embed }">
								<i *ngIf="icon" class="{{icon}} mr-2 text-secondary"></i>
								{{modalTitle}}
							</h3>
							<div class="ml-3">
								<ng-content select="[ui-modal-sub-header]"></ng-content>
							</div>
						</div>

						<div [ngClass]="{'flex-weight-1' : alignHeaderContentNearTitle}">
							<ng-content select="[ui-modal-header]"></ng-content>
						</div>
						<button *ngIf="showCloseButton && !embed" type="button" class="btn btn-outline-secondary border-0 p-2 mr-n2" (click)="shouldClose()">
							<i class="wq wq-close"></i>
						</button>
					</div>
					<div *ngIf="header" class="hr" [ngClass]="{ 'mx-4': !embed }"></div>
					<div [ngClass]="{ 'px-4': !embed }"><ng-content select="[ui-modal-sticky-header]"></ng-content></div>
					<div class="py-4" [ngClass]="{ 'modal-body px-4': !embed, 'flex-weight-1 scrollable': embed }">
						<ng-content></ng-content>
					</div>
					<div *ngIf="footer" class="border-top-0 pt-0" [ngClass]="{ 'modal-footer px-4 pb-4': !embed, 'd-flex': embed }">
						<ng-content select="[ui-modal-footer]"></ng-content>
					</div>
				</div>
				<ng-content *ngIf="customContent" select="[ui-modal-custom-content]"></ng-content>
			</div>
		</div>
	`,
	styles: [`
		.modal-full-height { min-height: 100%; }
		.modal-overflow-visible { overflow: visible; }
		.modal-overflow-visible * { overflow: visible; }
	`],
	providers: [DataGuardService]
})
export class UIModalComponent implements OnInit, AfterViewInit {

	@ViewChild('modal') modalElement: ElementRef;

	// Date and time pickers inside this modal. Most are appendTo="body", so their popup lives
	// outside the modal and is not hidden with it — see closePickers().
	@ContentChildren(Calendar, { descendants: true }) pickers: QueryList<Calendar>;

	@Input() modalTitle = '';
	@Input() footer = true;
	@Input() header = true;
	@Input() scrollable = false;
	@Input() size;
	@Input() icon = '';
	@Input() customContent = false;
	@Input() showBackButton = false;
	@Input() fullHeight = false;
	@Input() closeMessage = { title: 'Are you sure?', message: 'If you close this view now, you will lose all your changes' };
	@Input() overflowVisible = false;
	@Input() showCloseButton = true;
	@Input() easyClose = true; // Controls whether modal can be closed via the escape key or by clicking on the backdrop.
	@Input() embed = false;
	@Input() checkForUnsavedChanges = false;
	@Input() dirty = false;
	@Input() alignHeaderContentNearTitle = false;

	@Output() back = new EventEmitter<any>();

	onClose = new EventEmitter<any>();

	onShow = new EventEmitter<any>();
	onHide = new EventEmitter<any>();

	isOpen = false;

	constructor(
		public dataGuard: DataGuardService,
		public app: AppService
	) { }

	ngOnInit(): void { }

	ngAfterViewInit() {
		$(!this.embed && this.modalElement?.nativeElement).on('show.bs.modal', () => { this.onShow.emit(); });
		$(!this.embed && this.modalElement?.nativeElement).on('hide.bs.modal', () => this.closePickers());
		$(!this.embed && this.modalElement?.nativeElement).on('hidden.bs.modal', () => {
			// This should fix scrollbar issues with multiple modals.
			// Bootstrap removes this class by default when the top-most modal is closed,
			// but it should stay as long as there are any modals open.
			if ($('.modal:visible').length) $(document.body).addClass('modal-open');

			this.onHide.emit();
		});
		$(!this.embed && this.modalElement?.nativeElement).on('hidePrevented.bs.modal', () => {
			if (this.easyClose) {
				this.shouldClose();
			}
		});
	}

	open() {
		if (this.isOpen) return;
		this.isOpen = true;
		$(!this.embed && this.modalElement?.nativeElement).data('modalComponent', this);
		$(!this.embed && this.modalElement?.nativeElement).data('bs.modal', null);
		$(!this.embed && this.modalElement?.nativeElement).modal({ keyboard: false, backdrop: 'static' });
		$(!this.embed && this.modalElement?.nativeElement).modal('show');
	}

	close(data: any = null, triggerEvent = true) {
		this.closePickers();
		$(!this.embed && this.modalElement?.nativeElement).data('modalComponent', null);
		$(!this.embed && this.modalElement?.nativeElement).modal('hide');
		this.setDirty(false);
		if (triggerEvent) this.onClose.emit(data);
		if (!this.embed) this.isOpen = false;

		// Focus topmost modal element. Useful when closing the top modal of a stack of open modals.
		// If we don't do this, no element will have focus, therefore the next modal can't be closed
		// by pressing the escape key.
		$('.modal:visible').last().focus();
	}

	/**
	 * Close any picker popup that is still open. A picker appended to the body closes on an
	 * outside click, but a click inside the modal does not reach its listener, so typing a time
	 * and pressing Save left the popup floating over the page after the modal had gone.
	 */
	closePickers() {
		this.pickers?.forEach(picker => {
			if (picker.overlayVisible) picker.hideOverlay();
		});
	}

	async shouldClose() {
		await this.dataGuard.confirm(this.closeMessage.title, this.closeMessage.message).then((choice) => {
			if (choice) this.close();
		});
	}

	setDirty(isDirty = true) {
		this.dataGuard.dataDirty = isDirty;
	}

	backdropClick(event) {
		if ($(event.target).is('.modal')) {
			event.stopImmediatePropagation();
		}
	}

	goBack() {
		if (!this.checkForUnsavedChanges) this.back.emit();
		else {
			if (this.dirty) {
				this.app.dialog.pop(this.closeMessage.title, this.closeMessage.message, ['Cancel', '*Leave'], btn => {
					if (btn === 1) {
						this.back.emit();
					} else {
						return;
					}
				});
			} else {
				this.back.emit();
			}
		}
	}

}
